import { useRef, useState, memo, useCallback } from "react";
import { TextareaAutosize } from "@mui/base/TextareaAutosize";
import { useNotePageContextSelector } from "../../../hooks/NotePageContext";
import styles from "./noteTitle.module.css";
import { UNTITLED } from "@knowt/syncing/utils/dataCleaning";
import { toast } from "react-hot-toast";
import { useDebounce } from "react-use";

const NOTE_TITLE_ID = "note-main-title";
export const getNoteMainTitleText = () => {
    const noteTitle = document.getElementById(NOTE_TITLE_ID);
    return (noteTitle?.textContent || "").trim();
};

const NoteMainTitle = ({ onGoToNextInput, style }: { onGoToNextInput: () => void; style?: React.CSSProperties }) => {
    const ref = useRef<HTMLTextAreaElement>(null);

    const note = useNotePageContextSelector(state => state.note);
    const nonEditable = useNotePageContextSelector(state => state.nonEditable);
    const isNoteOwner = useNotePageContextSelector(state => state.isNoteOwner);
    const update = useNotePageContextSelector(state => state.update);

    const [localTitle, setLocalTitle] = useState(note?.title);

    useDebounce(
        () => {
            if (localTitle === note?.title) return;

            update({ title: localTitle });
        },
        500,
        [localTitle]
    );

    const saveAndGoToNextInput = useCallback(
        event => {
            if (localTitle === note?.title) {
                event.preventDefault();
                onGoToNextInput();
                return;
            }

            update({ title: localTitle });
            onGoToNextInput();
        },
        [localTitle]
    );

    const handleKeyDown = useCallback(
        event => {
            if (["Enter", "Tab", "ArrowDown"].includes(event.key)) {
                event.preventDefault();
                return saveAndGoToNextInput(event);
            }

            if (event.key === "ArrowRight") {
                const caretPos = ref?.current?.selectionEnd;
                const textContent = ref?.current?.value;

                if (caretPos === textContent?.length) {
                    return saveAndGoToNextInput(event);
                }
            }
        },
        [saveAndGoToNextInput]
    );

    const wasTextSelected = useRef(false);

    if (nonEditable) {
        return (
            <h1
                id={NOTE_TITLE_ID}
                className={styles.title}
                style={style}
                onMouseDown={() => {
                    const selection = window.getSelection();
                    wasTextSelected.current = selection?.type === "Range";
                }}
                onClick={() => {
                    const selection = window.getSelection();
                    if (isNoteOwner && selection?.type !== "Range" && !wasTextSelected.current) {
                        toast.error(
                            "Sorry this note is locked for editing since it's a part of our study hub, please contact us at Knowt.com/contact"
                        );
                    }
                }}
                data-testid="auto-id-3a0fe527-147d-4d4a-a28f-ed56fb6a551c">
                {localTitle ?? UNTITLED}
            </h1>
        );
    }

    return (
        <TextareaAutosize
            id={NOTE_TITLE_ID}
            ref={ref}
            value={localTitle || ""}
            minRows={1}
            placeholder={"Add a title"}
            className={styles.title}
            disabled={nonEditable}
            onChange={e => setLocalTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={150}
            style={style}
        />
    );
};

export default memo(NoteMainTitle);
